// The clocks on the players' lines, without React: at the position shown,
// each player's time left is what their clock showed after their last move
// at or before that ply (the opponent's after theirs), so stepping through
// the game shows the clocks ticking down as in a replay; at the start both
// show the game's initial time when the time control gives one. A game whose
// PGN has no clocks shows none. While the reader is on a line of their own,
// or a puzzle is on, the clocks are the branch point's, the last game
// position, unchanged; looking back (`browse`) along the game reads the
// clocks at the position looked at. Formatted as chess.com shows them:
// "4:32", "0:08", "1:02:10" over an hour, and tenths under ten seconds only
// when the PGN has them ("0:09.5").
import type { Color } from '@chess/api-model';
import { sideToMove, type ReviewGame, type ReviewState } from '@chess/api-review';

export type ClockedGame = Pick<ReviewGame, 'moves' | 'positions'> & Partial<Pick<ReviewGame, 'clocks' | 'initialClock'>>;

/** Whether the game has any clock at all; without one nothing is shown, not even the initial time. */
export function hasClocks(game: ClockedGame): boolean {
  return !!game.clocks?.some((c) => c !== undefined);
}

/**
 * Seconds `color` had left at game ply `ply` (0 is the start): their clock
 * after their last clocked move at or before the ply, else the initial time,
 * else undefined. Undefined for a game without clocks.
 */
export function clockAt(game: ClockedGame, ply: number, color: Color): number | undefined {
  if (!hasClocks(game)) return undefined;
  const clocks = game.clocks ?? [];
  for (let p = Math.min(ply, game.moves.length); p >= 1; p--) {
    if (game.moves[p - 1].color !== color) continue;
    const seconds = clocks[p - 1];
    if (seconds !== undefined) return seconds;
  }
  return game.initialClock;
}

const two = (n: number) => String(n).padStart(2, '0');

/** "4:32", "0:08", "1:02:10" over an hour; tenths under ten seconds only when `seconds` has a fraction ("0:09.5"). */
export function formatClock(seconds: number): string {
  const total = Math.max(0, seconds);
  const whole = Math.floor(total);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const tenths = total < 10 && total !== whole ? `.${Math.floor((total - whole) * 10)}` : '';
  return h > 0 ? `${h}:${two(m)}:${two(s)}${tenths}` : `${m}:${two(s)}${tenths}`;
}

/**
 * The game ply the clocks are read at: the ply shown, the branch point while
 * a line is open or a puzzle is on, and the ply looked back at while
 * browsing (never past the branch point).
 */
export function clockPly(state: Pick<ReviewState, 'ply' | 'exploring' | 'browse'>): number {
  const branch = state.exploring ? state.exploring.fromPly : state.ply;
  return state.browse === undefined ? branch : Math.min(state.browse, branch);
}

/** What the two lines show: each player's clock as text, and whose is running (the side to move at the clocks' ply). */
export interface PlayerClocks {
  white?: string;
  black?: string;
  running: Color;
}

/** The clocks for the review's state; none for a game without clocks or with none open. */
export function playerClocks(state: Pick<ReviewState, 'game' | 'ply' | 'exploring' | 'browse'>): PlayerClocks | undefined {
  const game = state.game;
  if (!game || !hasClocks(game)) return undefined;
  const ply = clockPly(state);
  const fen = game.positions[ply];
  const running = fen ? sideToMove(fen) : ply % 2 === 0 ? 'white' : 'black';
  const text = (color: Color) => {
    const seconds = clockAt(game, ply, color);
    return seconds === undefined ? undefined : formatClock(seconds);
  };
  const white = text('white');
  const black = text('black');
  return { ...(white === undefined ? {} : { white }), ...(black === undefined ? {} : { black }), running };
}
